import { ref } from "vue";
import { defineStore } from "pinia";
import { useToast } from "vue-toastification";
import apiClient from '@/axios';

const toast = useToast();

export const useSettingStore = defineStore("settings", () => {
    const settingEntity = ref({});

    const CACHE_KEY = 'app_settings_cache';
    const CACHE_DURATION = 10 * 60 * 1000; // 10 دقیقه

    async function fetchSetting(forceRefresh = false) {
        // اگر کش معتبر وجود دارد و فورس رفرش نیست
        if (!forceRefresh && isValidCacheAvailable()) {
            const cached = getFromCache();
            settingEntity.value = cached;
            return cached;
        }

        try {
            const response = await apiClient.get(`/Setting/GetByFId?id=Web_Setting`);
            settingEntity.value = response.data;

            // ذخیره در کش
            saveToCache(response.data);
            console.log('🔄 Fresh settings fetched from server');

            return response.data;
        } catch (error) {
            console.error("Error fetching Setting:", error);

            // اگر خطا خورد، از کش استفاده کن
            const cached = getFromCache();
            if (cached) {
                settingEntity.value = cached;
                console.log('🔄 Using cached settings due to server error');
                return cached;
            }
            return null;
        }
    }

    async function saveOrUpdateSetting(settingsData) {
        try {
            const settingPayload = {
                printSettings: JSON.stringify(settingsData.printSettings),
                generalSettings: JSON.stringify(settingsData.generalSettings),
                smsSettings: JSON.stringify(settingsData.smsSettings),
                fId: "Web_Setting"
            };

            const existingSettings = await fetchSetting();

            let response;
            if (existingSettings && existingSettings.fId) {
                response = await apiClient.put('/Setting', {
                    ...settingPayload
                });
            } else {
                response = await apiClient.post('/Setting', {
                    ...settingPayload
                });
            }

            if (response.status === 200) {
                // 🔄 این خط رو تغییر دادم: force refresh برای آپدیت کش
                await fetchSetting(true); // true = force refresh from server

                return true;
            } else {
                toast.error("خطا در ذخیره تنظیمات");
                console.error(response.data);
                return false;
            }
        } catch (error) {
            toast.error("خطا در ذخیره تنظیمات");
            console.error(error);
            return false;
        }
    }

    function saveToCache(settings) {
        const cacheData = {
            data: settings,
            timestamp: Date.now()
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    }

    function getFromCache() {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;

        try {
            const cacheData = JSON.parse(cached);
            return cacheData.data;
        } catch {
            return null;
        }
    }

    function isValidCacheAvailable() {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return false;

        try {
            const cacheData = JSON.parse(cached);
            const isExpired = Date.now() - cacheData.timestamp > CACHE_DURATION;
            return !isExpired;
        } catch {
            return false;
        }
    }

    function clearCache() {
        localStorage.removeItem(CACHE_KEY);
        console.log('🗑️ Settings cache cleared');
    }

    function hasValidCache() {
        return isValidCacheAvailable();
    }

    async function addSetting(settingData) {
        return await saveOrUpdateSetting(settingData);
    }

    async function updateSetting(input) {
        return await saveOrUpdateSetting(input);
    }

    function parseSettings(settingsEntity) {
        if (!settingsEntity) return null;

        try {
            return {
                printSettings: settingsEntity.printSettings ? JSON.parse(settingsEntity.printSettings) : {},
                generalSettings: settingsEntity.generalSettings ? JSON.parse(settingsEntity.generalSettings) : {},
                smsSettings: settingsEntity.smsSettings ? JSON.parse(settingsEntity.smsSettings) : {}
            };
        } catch (error) {
            console.error("Error parsing settings JSON:", error);
            return {
                printSettings: {},
                generalSettings: {},
                smsSettings: {}
            };
        }
    }

    return {
        settingEntity,
        fetchSetting,
        addSetting,
        updateSetting,
        saveOrUpdateSetting,
        parseSettings,
        clearCache,
        hasValidCache
    }
});